import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/client";
import { formatRelativeShort } from "../utils/datetime";
import ModalBackdrop from "./ModalBackdrop";

/**
 * Choose the ticket to merge into, and see what will move, before committing.
 *
 * WHAT THIS REPLACES. A `window.prompt` asking for a ticket number, followed by
 * a `window.confirm`. The comment defending that choice argued a technician who
 * has decided two tickets are duplicates already has the other one open and can
 * read the number off a tab — reasonable, and it did not survive contact with
 * use: the owner of the product asked for ticket merging as a NEW feature,
 * which is what an undiscoverable feature looks like from the outside.
 *
 * WHY A PREVIEW AND NOT JUST A PICKER. The merge cannot be undone from the
 * interface. It moves logged time, invoice lines and attachments onto another
 * ticket. Someone about to do that should see the inventory first, not read it
 * in the audit log afterwards.
 *
 * THE DIRECTION IS THE DANGEROUS PART. One of these two tickets is closed and
 * stops existing as a place to work; the other absorbs everything. Every
 * surface here states which is which in words rather than relying on the user
 * to remember what "merge into" means — the heading, the preview line, and the
 * final button all name the survivor explicitly.
 */

type Candidate = {
  id: number;
  title: string | null;
  status: string | null;
  priority: string | null;
  requester: string | null;
  assignee: string | null;
  created_at: string | null;
  updated_at: string | null;
  comment_count: number;
};

type Preview = {
  source_id: number;
  target_id: number;
  requested_target_id: number;
  retargeted: boolean;
  counts: Record<string, number>;
  moves: string[];
  source_title: string | null;
  target_title: string | null;
  target_will_reopen: boolean;
};

const STATUS_CLS: Record<string, string> = {
  open: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  on_hold: "bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200",
  resolved: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  closed: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

export default function TicketMergeModal({
  sourceId,
  sourceTitle,
  onClose,
  onMerged,
}: {
  sourceId: number;
  sourceTitle: string | null;
  onClose: () => void;
  /** Called with the surviving ticket id once the merge has gone through. */
  onMerged: (survivorId: number) => void;
}) {
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<Candidate | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState("");

  // Debounced so typing a subject does not fire a request per keystroke.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(
    async (term: string) => {
      setLoading(true);
      setError("");
      try {
        const r = await api.get(`/api/v1/tickets/${sourceId}/merge-candidates`, {
          params: { q: term },
        });
        setCandidates(r.data.candidates);
      } catch (e: any) {
        // Say what failed. A blank list would read as "no duplicates exist",
        // which is a different and wrong answer.
        setCandidates(null);
        setError(
          e?.response?.data?.detail ||
            "Could not load the list of tickets to merge into."
        );
      } finally {
        setLoading(false);
      }
    },
    [sourceId]
  );

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), q ? 250 : 0);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [q, search]);

  // Picking a ticket immediately asks the server what would move. The preview
  // runs the same validation as the merge, so an impossible choice is refused
  // here with the real reason instead of at the final button.
  useEffect(() => {
    if (!picked) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    setError("");
    api
      .get(`/api/v1/tickets/${sourceId}/merge-preview`, { params: { into: picked.id } })
      .then((r) => {
        if (!cancelled) setPreview(r.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setPreview(null);
        setError(e?.response?.data?.detail || "That merge is not possible.");
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [picked, sourceId]);

  const doMerge = async () => {
    if (!preview) return;
    setMerging(true);
    setError("");
    try {
      await api.post(`/api/v1/tickets/${sourceId}/merge`, {
        // The id the preview resolved to, not the one clicked: merging into an
        // already-merged ticket follows the chain, and the confirmation above
        // has already told the user where it will land.
        into_ticket_id: preview.target_id,
      });
      onMerged(preview.target_id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "That merge could not be completed.");
      setMerging(false);
    }
  };

  const nothingToMove = useMemo(
    () => !!preview && preview.moves.length === 0,
    [preview]
  );

  return (
    <ModalBackdrop onClose={merging ? () => {} : onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Merge ticket"
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Merge ticket #{sourceId}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Pick the ticket this one is a duplicate of.{" "}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              #{sourceId}
              {sourceTitle ? ` (${sourceTitle})` : ""} will be closed
            </span>{" "}
            and everything on it moves to the ticket you choose.
          </p>
        </div>

        {error && (
          <div className="mx-5 mt-4 px-3 py-2 rounded text-sm bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="px-5 pt-4">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by subject, or type a ticket number…"
            className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Only tickets for the same customer are shown — a merge across
            customers is refused, so offering one would waste your time.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-[8rem]">
          {loading && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Searching…</div>
          )}
          {!loading && candidates && candidates.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {q
                ? "No other tickets for this customer match that."
                : "This customer has no other open tickets to merge into."}
            </div>
          )}
          {!loading &&
            candidates?.map((c) => {
              const active = picked?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setPicked(active ? null : c)}
                  className={`w-full text-left px-3 py-2 rounded border mb-1.5 transition-colors ${
                    active
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                      : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      #{c.id}
                    </span>
                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                      {c.title || "(no subject)"}
                    </span>
                    <span
                      className={`ml-auto shrink-0 text-[11px] px-2 py-0.5 rounded-full ${
                        STATUS_CLS[c.status || ""] ||
                        "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {(c.status || "unknown").replace("_", " ")}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-3">
                    {c.requester && <span>{c.requester}</span>}
                    {c.assignee && <span>assigned to {c.assignee}</span>}
                    <span>
                      {c.comment_count} {c.comment_count === 1 ? "reply" : "replies"}
                    </span>
                    {/* formatRelativeShort, not new Date(): the API sends
                        zone-less UTC and the browser would read it as local. */}
                    {c.updated_at && <span>updated {formatRelativeShort(c.updated_at)}</span>}
                  </div>
                </button>
              );
            })}
        </div>

        {/* What will actually happen. Shown only once a target is chosen. */}
        {picked && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-sm">
            {previewing && (
              <div className="text-gray-500 dark:text-gray-400">
                Checking what would move…
              </div>
            )}
            {!previewing && preview && (
              <>
                <div className="text-gray-900 dark:text-gray-100">
                  <span className="font-semibold">#{preview.source_id}</span> will be
                  closed and merged into{" "}
                  <span className="font-semibold">
                    #{preview.target_id}
                    {preview.target_title ? ` — ${preview.target_title}` : ""}
                  </span>
                  .
                </div>
                {preview.retargeted && (
                  <div className="mt-1 text-amber-700 dark:text-amber-400">
                    #{preview.requested_target_id} has itself already been merged, so
                    this will land on #{preview.target_id}, where the conversation
                    actually is.
                  </div>
                )}
                <div className="mt-1 text-gray-600 dark:text-gray-400">
                  {nothingToMove
                    ? "Nothing is attached to #" +
                      preview.source_id +
                      " — only the ticket itself moves."
                    : "Moving: " + preview.moves.join(", ") + "."}
                </div>
                {preview.target_will_reopen && (
                  <div className="mt-1 text-gray-600 dark:text-gray-400">
                    #{preview.target_id} is closed and will be reopened, because the
                    merge means there is live work on it.
                  </div>
                )}
                <div className="mt-1 text-gray-500 dark:text-gray-400">
                  This cannot be undone from here.
                </div>
              </>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={merging}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={doMerge}
            disabled={!preview || previewing || merging}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {merging
              ? "Merging…"
              : preview
              ? `Merge #${preview.source_id} into #${preview.target_id}`
              : "Merge"}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
